import React from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';
import { useParams, useHistory } from 'react-router-dom';

import { Modal } from 'ui/base/modal';
import { FormSubmitEvent } from 'ui/base/types';
import { ToastID, defaultToast } from 'ui/base/toast';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';

import { PlainButton } from 'ui/public/button';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { SwitchInput } from 'ui/standard/input/switch_input/switch_input';
import { BandwidthInput } from 'ui/standard/input/bandwidth_input/bandwidth_input';

import { ResultKind } from 'services/base/types';
import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import UploadIcon from './assets/network/upload.png';
import DownloadIcon from './assets/network/download.png';

import {
  BandwidthControlsInformationModal,
  BufferBloatInformationModal,
} from './bandwidth_settings_modal';

import Messages from './bandwidth_settings_messages';
import baseStyles from './bandwidth_settings.module.css';
import { BandwidthSettingsStore } from './bandwidth_settings_store';

type BandwidthSettingsStyles = {
  headerRow: string;
  headerText: string;
  save: string;
  container: string;
  row: string;
  inputGroup: string;
  icon: string;
  unit: string;
  column: string;
  buttonContainer: string;
  labelText: string;
  text: string;
  supportIcon: string;
  input: string;
};

const themedStyles: ThemedStyles<BandwidthSettingsStyles> = {
  [Theme.DAYLIGHT]: {
    headerRow: baseStyles.baseHeaderRow,
    headerText: baseStyles.daylightHeaderText,
    save: baseStyles.daylightSave,
    container: baseStyles.daylightContainer,
    row: baseStyles.baseRow,
    inputGroup: baseStyles.inputGroup,
    icon: baseStyles.baseIcon,
    unit: baseStyles.daylightUnit,
    buttonContainer: baseStyles.baseButtonContainer,
    column: baseStyles.baseColumn,
    labelText: baseStyles.daylightLabelText,
    text: baseStyles.daylightText,
    supportIcon: baseStyles.daylightSupportIcon,
    input: baseStyles.input,
  },
  [Theme.MIDNIGHT]: {
    headerRow: baseStyles.baseHeaderRow,
    headerText: baseStyles.midnightHeaderText,
    save: baseStyles.midnightSave,
    container: baseStyles.midnightContainer,
    row: baseStyles.baseRow,
    inputGroup: baseStyles.inputGroup,
    icon: baseStyles.baseIcon,
    unit: baseStyles.midnightUnit,
    buttonContainer: baseStyles.baseButtonContainer,
    column: baseStyles.baseColumn,
    labelText: baseStyles.midnightLabelText,
    text: baseStyles.midnightText,
    supportIcon: baseStyles.midnightSupportIcon,
    input: baseStyles.input,
  },
};

type BaseBandwidthSettingsProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};
export const BaseBandwidthSettings = observer(({ accounts, pangolin }: BaseBandwidthSettingsProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new BandwidthSettingsStore(pangolin));
  React.useEffect(() => { setStore(new BandwidthSettingsStore(pangolin)); }, [pangolin]);

  const onShowBufferBloatModal = React.useCallback(() => {
    store.bufferbloatPreventionModal.showModal();
  }, [store]);

  const onShowBandwidthControlModal = React.useCallback(() => {
    store.bandwidthControlsModal.showModal();
  }, [store]);

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(accounts));
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      history.goBack();
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 400) {
      // do nothing
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
    }
  }, [store, accounts, history]);

  return (
    <StandardPage>
      <Modal store={store.bufferbloatPreventionModal}>
        <BufferBloatInformationModal store={store.bufferbloatPreventionModal}/>
      </Modal>
      <Modal store={store.bandwidthControlsModal}>
        <BandwidthControlsInformationModal store={store.bandwidthControlsModal}/>
      </Modal>
      <form onSubmit={onSubmit}>
        <div className={styles.headerRow}>
          <BackButton/>
          <div className={styles.headerText}>
            {Messages.bandWidthSettings()}
          </div>
          <PlainButton
            type='submit'
            busy={store.busy} reserveBusyIconArea={true}
            className={styles.save}>
            {Messages.save()}
          </PlainButton>
        </div>
        <div className={styles.container}>
          <div className={styles.inputGroup}>
            <div className={styles.row}>
              <img src={DownloadIcon} className={styles.icon} alt='download' />
              <div className={styles.input}>
                <BandwidthInput store={store.download}/>
              </div>
            </div>
            <ErrorMessage message={store.bandwidthDownloadStringError}/>
          </div>
          <div className={styles.inputGroup}>
            <div className={styles.row}>
              <img src={UploadIcon} className={styles.icon} alt='upload' />
              <div className={styles.input}>
                <BandwidthInput store={store.upload}/>
              </div>
            </div>
            <ErrorMessage message={store.bandwidthUploadStringError}/>
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.buttonContainer}>
            <div className={styles.row}>
              <div className={styles.labelText}>
                {Messages.enableAntiBufferBloat()}
              </div>
              <button type='button' className={styles.supportIcon} onClick={onShowBufferBloatModal}/>
            </div>
            <SwitchInput store={store.enableBufferbloatPrevention}/>
          </div>
          <div className={styles.text}>
            {Messages.antiBufferBloatInstructions()}
          </div>
        </div>
        <div className={styles.column}>
          <div className={styles.buttonContainer}>
            <div className={styles.row}>
              <div className={styles.labelText}>
                {Messages.enableBandwidthControls()}
              </div>
              <button type='button' className={styles.supportIcon} onClick={onShowBandwidthControlModal}/>
            </div>
            <SwitchInput store={store.enableBandwidthControls}/>
          </div>
          <div className={styles.text}>
            {Messages.bandwidthControlsInstructions()}
          </div>
        </div>
      </form>
    </StandardPage>
  );
});

export const BandwidthSettings = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseBandwidthSettings
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
